import { Routes, Route, Navigate } from "react-router-dom";
import { Frame } from "@shopify/polaris";
import AppLayout from "./pages/app/app-layout";
import LoginPage from "./pages/auth/login/login";
import Users from "./features/users/users";
import UserContainer from "containers/user/user-container";
import Customers from "./features/customers/customers";
import { Toaster } from "sonner";
import "App.scss";
import Dashboard from "./pages/app/dashboard/Dashboard";
import CompanyDashboard from "./pages/app/dashboard/CompanyDashboard";
import CustomerContainer from "containers/customer/customer-container";
import FinanceDetailContainer from "containers/finance-detail/finance-detail-container";
import Products from "containers/products/products";
import FilesTemplatesContainer from "containers/files-templates/files-templates-container";
import FilesTemplatePage from "containers/files-template-form/files-template-page";
import Reservations from "containers/reservations/reservations";
import ProjectBoards from "containers/project-boards/project-boards";
import ProjectBoardContainer from "containers/project-board/project-board-container";
import ProductDetail from "containers/products/product-detail";
import CustomerForm from "pages/customer-form/customer-form";
import ProjectContainer from "containers/project-view-container/project-view-container";
import ProjectOpportunityHistory from "containers/project-opportunity-history/project-opportunity-history";
import Documents from "containers/document/category-tree/tree-container";
import DocumentCartPage from "containers/document-cart/document-cart-page";
import FileContainer from "containers/file/file-view-container";
import CustomFieldContainer from "containers/custom-field/custom-field-container";
import SeriesContainer from "containers/files-series/files-series-container";
import InvoiceSeriesContainer from "containers/invoice-series/invoice-series-container";
import Settings from "containers/settings/settings-container";
import SettingsLayout from "containers/settings/SettingsLayout";
import SettingsIndexRedirect from "containers/settings/SettingsIndexRedirect";
import { BusinessTripSettingsRedirect } from "containers/settings/business-trip-settings-container";
import {
  OrdersSettingsRedirect,
  OrderAmendmentSettingsRedirect,
} from "containers/settings/orders-settings-container";
import { FuelReportSettingsRedirect } from "containers/settings/fuel-report-settings-container";
import AuditLogSettingsContainer from "containers/audit-log/audit-log-settings-container";
import ValidationAttemptCustomersContainer from "containers/customer-form-validation-attempts/validation-attempt-customers-container";
import ValidationAttemptsByCustomerContainer from "containers/customer-form-validation-attempts/validation-attempts-by-customer-container";
import AboutCompany from "containers/about-company/company-about-route";
import FilesTypes from "containers/files-types/files-types-container";
import { PermissionsGroupsRedirect } from "containers/settings/tabs/PermissionsSection";
import DepartmentsPage from "containers/departments/departments-page";
import DepartmentDetailPage from "containers/departments/department-detail-page";
import ProtectedRoute from "components/protectedRoute/ProtectedWrap";
import { profileRoutes } from "containers/user-profile/profile-routes";
import Assets from "containers/assets/assets";
import AssetContainer from "containers/asset-view-container/asset-view-container";
import AssetsTypes from "containers/assets-types/assets-types-container";
import { tasksRoutes, noCompanyTasksRoute } from "features/cabinet-tasks/tasks-routes";
import UnsignedFilesContainer from "containers/unsigned-files/unsigned-files-container";
import Welcome from "features/welcome/Welcome";
import OrderContainer from "containers/order-view-container/order-view-container";
import Orders from "features/orders/orders";
import OrderForm from "features/order-form/order-form";
import CompanyCreate from "features/add-company/add-company";
import GlobalFileViewContainer from "containers/file-view-universal/file-view-container";
import FuelReports from "containers/fuel-reports/fuel-reports";
import FuelReportContainer from "containers/fuel-report-view-container/fuel-report-view-container";
import BusinessTrips from "containers/business-trips/business-trips";
import BusinessTripContainer from "containers/business-trip-view-container/business-trip-view-container";
import WorkSecurityRoles from "containers/work-security/work-security";
import WorkSecurityRoleContainer from "containers/work-security-view-container/work-security-view-container";
import WorkSecurityIncidentViewContainer from "containers/work-security-incident-view-container/work-security-incident-view-container";
import WorkSecurityTrainingViewContainer from "containers/work-security-training-view-container/work-security-training-view-container";
import DeskSettings from "containers/desk-settings/desk-settings";
import InvoiceInbox from "containers/invoice-inbox/invoice-inbox";
import OutgoingInvoices from "containers/outgoing-invoices/outgoing-invoices";
import InvoiceInboxSettings from "containers/invoice-inbox/invoice-inbox-settings";
import DeskInbox, { DeskArchive } from "containers/desk-inbox/desk-inbox";
import DeskToday from "containers/desk-today/desk-today";
import SignontabUsers from "containers/signontab-users/signontab-users-container";
import ProductGroups from "features/product-groups/product-groups";
import Posts from "features/posts/Posts";
import Recognition from "features/recognition/Recognition";
import LeaveCalendarPage from "features/leave-calendar/LeaveCalendarPage";
import CompanyHolidaysSettingsPage from "features/company-holidays/CompanyHolidaysSettingsPage";
import Onboardings from "containers/onboardings/onboardings";
import OnboardingViewContainer from "containers/onboarding-view-container/onboarding-view-container";
import PositionsPage from "features/positions/PositionsPage";
import AssetRequestsPage from "features/asset-requests/AssetRequestsPage";
import PersonnelListPage from "features/personnel/PersonnelListPage";
import PersonnelCardPage from "features/personnel/PersonnelCardPage";
import {
  DEPARTMENTS_VIEW_GROUPS,
  ONBOARDING_LIST_GROUPS,
  PERSONNEL_PERMISSION_GROUPS,
  POSITIONS_VIEW_GROUPS,
} from "features/personnel/personnel-helpers";
import WsApprovalPanel from "features/work-security-approvals/WsApprovalPanel";
import OnboardingKiosk from "pages/onboarding-kiosk/onboarding-kiosk";
import GuestCall from "features/calls/guest-call";
import OnboardingLayout from "pages/app/onboarding-layout";
import UserMerge from "features/users/user-merge/UserMerge";
import UserMergeHistoryContainer from "containers/user-merge-history/user-merge-history-container";
import UserMergeHistoryDetailContainer from "containers/user-merge-history/user-merge-history-detail-container";
import ScrollRestoration from "components/scroll-restoration/scroll-restoration";
import useIsPhone from "features/files/use-is-phone";

function App() {
  const isPhone = useIsPhone();

  return (
    <Frame>
      <ScrollRestoration />
      {/* Offset below the topbar and clear of the presence rail — otherwise a
          toast draws over the topbar, the rail, and (worst) a panel header's
          own Close button underneath it. Both vars are read live: the topbar
          height is fixed in DashboardLayout.scss, the rail width is published
          by OnlineUsersPanel only while one is mounted (falls back to 0). */}
      <Toaster
        position="top-right"
        richColors
        closeButton
        // A phone screen takes longer to read a long message on, and sonner has no
        // separate time for errors.
        duration={isPhone ? 8000 : 4000}
        offset={{
          top: "calc(var(--dashboard-topbar-height, 4rem) + 12px)",
          right: "calc(var(--presence-rail-width, 0px) + 16px)",
        }}
        // On a phone (sonner's mobile layout is up to 600px) there is no rail, so
        // the stack spans the screen with a small margin on both sides.
        mobileOffset={{
          top: "calc(var(--dashboard-topbar-height, 4rem) + 12px)",
          left: 8,
          right: 8,
        }}
      />
      <Routes>
        <Route path="/" element={<Navigate to="/app" />} />
        <Route path="auth/login" element={<LoginPage />} />
        {/* Sign-up and password restore are gone (sign-in is by an e-mail link); old links go to the login */}
        <Route path="auth/signup" element={<Navigate to="/auth/login" replace />} />
        <Route path="auth/restore" element={<Navigate to="/auth/login" replace />} />
        <Route path="customer-form/:formKey" element={<CustomerForm />} />
        <Route path="onboarding/kiosk" element={<OnboardingKiosk />} />
        {/* A guest following a call link — no account, no session: the
            signed link is the whole authorization */}
        <Route path="call/:token" element={<GuestCall />} />
        <Route path="app/my-onboarding" element={<OnboardingLayout />} />
        <Route path="app" element={<AppLayout />}>
          <Route index element={<Welcome />} />
          <Route path="new-company-form" element={<CompanyCreate />} />
          {noCompanyTasksRoute}
          <Route path="unsigned-files" element={<UnsignedFilesContainer />} />
          <Route path=":companyId" element={<Dashboard />}>
            <Route index element={<Navigate to="posts" replace />} />
            <Route
              element={
                <ProtectedRoute
                  roles={
                    ["Master", "Admin", "SalesMan", "Signature"] as string[]
                  }
                />
              }
            >
              <Route path="company" element={<CompanyDashboard />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={
                    [
                      "Master",
                      "Admin",
                      "SalesMan",
                      "Signature",
                      "Access all customers",
                    ] as string[]
                  }
                />
              }
            >
              <Route path="customers" element={<Customers />} />
              <Route
                path="customers/:customerId"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/documents"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/finance/:year/:financeId"
                element={<FinanceDetailContainer />}
              />
              <Route
                path="customers/:customerId/products"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/invoices"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/salesreturns"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/emails"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/settlements"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/reservations"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/orders"
                element={<CustomerContainer />}
              />
              <Route
                path="customers/:customerId/orders/new"
                element={<OrderForm />}
              />
              <Route
                path="customers/:customerId/orders/:orderId"
                element={<OrderContainer />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/edit"
                element={<OrderForm />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/invoices"
                element={<OrderContainer />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/products"
                element={<OrderContainer />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/fulfillment"
                element={<OrderContainer />}
              />
              {/* Redirects for old URLs */}
              <Route
                path="customers/:customerId/orders/:orderId/reservations"
                element={<Navigate to="../fulfillment" replace />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/deliveries"
                element={<Navigate to="../fulfillment" replace />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/emails"
                element={<OrderContainer />}
              />
              <Route
                path="customers/:customerId/orders/:orderId/amendments"
                element={<OrderContainer />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  roles={
                    [
                      "Master",
                      "Admin",
                      "Signature",
                      "Access all customers",
                    ] as string[]
                  }
                />
              }
            >
              <Route path="orders" element={<Orders />} />
              <Route path="orders/new" element={<OrderForm />} />
              <Route path="orders/:orderId" element={<OrderContainer />} />
              <Route path="orders/:orderId/edit" element={<OrderForm />} />
              <Route
                path="orders/:orderId/invoices"
                element={<OrderContainer />}
              />
              <Route
                path="orders/:orderId/products"
                element={<OrderContainer />}
              />
              <Route
                path="orders/:orderId/fulfillment"
                element={<OrderContainer />}
              />
              {/* Redirects for old URLs */}
              <Route
                path="orders/:orderId/reservations"
                element={<Navigate to="../fulfillment" replace />}
              />
              <Route
                path="orders/:orderId/deliveries"
                element={<Navigate to="../fulfillment" replace />}
              />
              <Route
                path="orders/:orderId/emails"
                element={<OrderContainer />}
              />
              <Route
                path="orders/:orderId/amendments"
                element={<OrderContainer />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  roles={
                    ["Master", "Admin", "SalesMan", "Signature"] as string[]
                  }
                />
              }
            >
              <Route path="products" element={<Products />}>
                <Route path=":productId" element={<ProductDetail />} />
              </Route>
              <Route
                path="product-groups"
                element={<ProductGroups />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  roles={
                    ["Master", "Admin", "SalesMan", "Signature"] as string[]
                  }
                />
              }
            >
              <Route path="reservations" element={<Reservations />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={["Master", "Access project"] as string[]}
                />
              }
            >
              <Route path="project-boards" element={<ProjectBoards />} />
              <Route
                path="project-boards/opportunity-history"
                element={<ProjectOpportunityHistory />}
              />
              <Route
                path="project-boards/:projectBoardId"
                element={<ProjectBoardContainer />}
              />
              <Route
                path="project-boards/:projectBoardId/project/:projectId"
                element={<ProjectContainer />}
              />
            </Route>
            {profileRoutes}
            <Route element={<ProtectedRoute roles={["Master"] as string[]} />}>
              <Route path="users/:userId" element={<UserContainer />} />
              <Route path="users/:userId/files" element={<UserContainer />} />
              <Route
                path="users/:userId/category-permission"
                element={<UserContainer />}
              />
              <Route
                path="users/:userId/customer-permission"
                element={<UserContainer />}
              />
              <Route
                path="users/:userId/customer-hide"
                element={<UserContainer />}
              />
              <Route
                path="users/:userId/onboarding-documents"
                element={<UserContainer />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  roles={["Master", "SalesMan", "Access document"] as string[]}
                />
              }
            >
              <Route
                path="documents/:catId/:fileId"
                element={<FileContainer />}
              />
            </Route>
            <Route
              path="documents/view/:fileId"
              element={<GlobalFileViewContainer />}
            />
            <Route
              element={
                <ProtectedRoute
                  roles={["Master", "SalesMan", "Access document"] as string[]}
                />
              }
            >
              <Route path="document-cart" element={<DocumentCartPage />} />
              <Route
                path="documents/*"
                element={<Documents source="document" />}
              />
            </Route>
            {/* Work Security list tabs are real routes: Roles, Assignments, and
                Signed Documents all render WorkSecurityRoles (tab from URL).
                Static paths outrank ":roleId", so the detail route still works. */}
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/assignments" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/signed-documents" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/archive" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/incidents" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route
                path="work-security/incidents/:incidentId"
                element={<WorkSecurityIncidentViewContainer />}
              />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/training" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/positions" element={<WorkSecurityRoles />} />
            </Route>
            {/* «Laukia patvirtinimo»: per-sphere approvals of position-derived
                pareigybės. The backend scopes both to the spheres the caller
                manages; the detail path is the one the approval email links to. */}
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/approvals" element={<WorkSecurityRoles />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/approvals/:approvalId" element={<WsApprovalPanel />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route
                path="work-security/training/:testId"
                element={<WorkSecurityTrainingViewContainer />}
              />
            </Route>
            {/* Desk — email-to-card task module (keep guard in sync with
                Menu.js isVisibleOnlyFor and backend config/policies.js) */}
            <Route element={<ProtectedRoute roles={["Master", "Access desk"] as string[]} />}>
              <Route path="desk" element={<Navigate to="today" replace />} />
              <Route path="desk/today" element={<DeskToday />} />
              <Route path="desk/inbox" element={<DeskInbox />} />
              <Route path="desk/shared" element={<DeskInbox mode="shared" />} />
              <Route path="desk/drafts" element={<DeskInbox mode="drafts" />} />
              <Route path="desk/archive" element={<DeskArchive />} />
              <Route path="desk/trash" element={<DeskInbox mode="trash" />} />
              <Route path="desk/settings" element={<DeskSettings />} />
            </Route>

            {/* Invoice Inbox — the purchase-document register (keep this guard
                in sync with Menu.js isVisibleOnlyFor and the backend's
                config/policies.js, which is the one that actually enforces it) */}
            <Route element={<ProtectedRoute roles={["Master", "Access invoice inbox"] as string[]} />}>
              <Route path="invoice-inbox" element={<InvoiceInbox />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={
                    [
                      "Master",
                      "Admin",
                      "Signature",
                      "Access all customers",
                    ] as string[]
                  }
                />
              }
            >
              <Route path="outgoing-invoices" element={<OutgoingInvoices />} />
            </Route>
            {/* Connecting a mailbox is a narrower right than reading the
                register — the backend checks the same permission again. */}
            <Route element={<ProtectedRoute roles={["Master", "Manage invoice inbox settings"] as string[]} />}>
              <Route path="invoice-inbox/settings" element={<InvoiceInboxSettings />} />
            </Route>
            <Route element={<ProtectedRoute roles={["Master", "Access work security assignments", "Access work security module"] as string[]} />}>
              <Route path="work-security/:roleId" element={<WorkSecurityRoleContainer />} />
            </Route>

            <Route element={<ProtectedRoute roles={["Master"] as string[]} />}>
              {/* Every settings page shares one layout with a grouped navigation
                  (see containers/settings/settings-sections.ts for the list).
                  Pages keep their own URLs; they are only wrapped. A static
                  path outranks "settings/:section", so settings/holidays and
                  the others below never reach the general settings screen. */}
              <Route element={<SettingsLayout />}>
                <Route path="settings" element={<SettingsIndexRedirect />} />
                <Route path="settings/:section" element={<Settings />} />
                <Route path="settings/holidays" element={<CompanyHolidaysSettingsPage />} />
                <Route path="settings/activity-log" element={<AuditLogSettingsContainer />} />
                <Route path="settings/user-merge" element={<UserMerge />} />
                <Route
                  path="settings/customer-form-validation-attempts"
                  element={<ValidationAttemptCustomersContainer />}
                />
                <Route
                  path="settings/customer-form-validation-attempts/:customerId"
                  element={<ValidationAttemptsByCustomerContainer />}
                />
                <Route path="users" element={<Users />} />
                <Route path="files/templates" element={<FilesTemplatesContainer />} />
                <Route path="files/templates/new" element={<FilesTemplatePage />} />
                <Route path="files/templates/:templateId" element={<FilesTemplatePage />} />
                <Route path="series" element={<SeriesContainer />} />
                {/* Sales invoice numbering. Separate from /series: that list names
                    files and contracts, this one issues legal invoice numbers. */}
                <Route path="invoice-series" element={<InvoiceSeriesContainer />} />
                <Route path="filestypes" element={<FilesTypes />} />
                <Route path="customfields" element={<CustomFieldContainer />} />
                <Route path="assetstypes" element={<AssetsTypes />} />
                <Route path="signontab-users" element={<SignontabUsers />} />
                <Route path="user-merge-history" element={<UserMergeHistoryContainer />} />
                <Route
                  path="user-merge-history/:mergeBatchId"
                  element={<UserMergeHistoryDetailContainer />}
                />
              </Route>
              {/* Old links to the settings tabs that had their own paths */}
              <Route
                path="settings/business-trip"
                element={<BusinessTripSettingsRedirect />}
              />
              <Route
                path="settings/order-amendment"
                element={<OrderAmendmentSettingsRedirect />}
              />
              <Route
                path="settings/orders"
                element={<OrdersSettingsRedirect />}
              />
              <Route
                path="settings/fuel-report"
                element={<FuelReportSettingsRedirect />}
              />
              {/* Permission groups are gone; the old address lands on the rights screen */}
              <Route path="permissions-groups" element={<PermissionsGroupsRedirect />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={["Master", "Access assets"] as string[]}
                />
              }
            >
              <Route path="assets" element={<Assets />} />
              <Route path="assets/requests" element={<AssetRequestsPage />} />
              <Route path="assets/:assetId" element={<AssetContainer />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={["Master", "Access fuel reports"] as string[]}
                />
              }
            >
              <Route path="fuel-reports" element={<FuelReports />} />
              <Route
                path="fuel-reports/:reportId"
                element={<FuelReportContainer />}
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={["Master", "Access business trips"] as string[]}
                />
              }
            >
              <Route path="business-trips" element={<BusinessTrips />} />
              <Route
                path="business-trips/:tripId"
                element={<BusinessTripContainer />}
              />
            </Route>
            <Route element={<ProtectedRoute roles={ONBOARDING_LIST_GROUPS} />}>
              <Route path="onboarding" element={<Onboardings />} />
              <Route
                path="onboarding/:onboardingId"
                element={<OnboardingViewContainer />}
              />
            </Route>
            {/* Personalas → Darbuotojai + the darbuotojo kortelė. Everyone who
                sees Personalas, plus heads of a department with data access
                (derived 'Access personnel (subordinates)'); the backend scopes
                the rows and answers 403 on a card outside the viewer's reach. */}
            <Route element={<ProtectedRoute roles={PERSONNEL_PERMISSION_GROUPS} />}>
              <Route path="personnel" element={<PersonnelListPage />} />
              <Route path="personnel/:userId" element={<PersonnelCardPage />} />
            </Route>
            {/* Personalas → Pareigos: the positions catalogue; WS reads it
                only (the page hides the CRUD — canManagePositions). */}
            <Route element={<ProtectedRoute roles={POSITIONS_VIEW_GROUPS} />}>
              <Route path="positions" element={<PositionsPage />} />
            </Route>

            <Route path="posts" element={<Posts />} />
            <Route path="recognition" element={<Recognition />} />
            <Route path="leave-calendar" element={<LeaveCalendarPage />} />
            <Route path="about-company" element={<AboutCompany />} />

            {/* Personalas → Skyriai; WS reads it only (canManageDepartments). */}
            <Route element={<ProtectedRoute roles={DEPARTMENTS_VIEW_GROUPS} />}>
              <Route path="departments" element={<DepartmentsPage />} />
              <Route
                path="departments/:id"
                element={<DepartmentDetailPage />}
              />
            </Route>


            <Route>
              {tasksRoutes}
              <Route
                path="unsigned-files"
                element={<UnsignedFilesContainer />}
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Frame>
  );
}

export default App;
